import type { SiteData } from '@/types/site';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

type DrawerType = 'site' | 'inspection' | 'detail' | null;

interface DrawerState {
  // 지속되는 상태들 (persist 대상)
  selectedSiteId: string | null;
  activeDrawer: DrawerType;
  drawerExpanded: boolean;
  selectedTimePeriod: string;

  // 런타임에만 존재하는 상태 (persist 하지 않음)
  selectedSite: SiteData | null;

  // Actions
  handleSiteClick: (site: SiteData) => void;
  closeDrawer: () => void;
  handleInspectionClick: (site: SiteData, event?: React.MouseEvent) => void;
  closeInspectionDrawer: () => void;
  handleDetailClick: (site: SiteData, event?: React.MouseEvent) => void;
  closeDetailDrawer: () => void;
  setDrawerExpanded: (expanded: boolean) => void;
  setSelectedTimePeriod: (period: string) => void;

  // 사이트 데이터 복원용
  restoreSiteData: (site: SiteData) => void;
  getSiteIdFromUrl: () => string | null;
}

export const useDrawerStore = create<DrawerState>()(
  persist(
    (set, get) => ({
      // 초기 상태
      selectedSiteId: null,
      activeDrawer: null,
      drawerExpanded: false,
      selectedTimePeriod: 'today',
      selectedSite: null,

      // 사이트 클릭 핸들러
      handleSiteClick: (site: SiteData) => {
        set({
          selectedSite: site,
          selectedSiteId: site.siteId,
          activeDrawer: 'site',
          selectedTimePeriod: 'today',
          drawerExpanded: false,
        });
      },

      // 드로어 닫기
      closeDrawer: () => {
        set({
          selectedSite: null,
          selectedSiteId: null,
          activeDrawer: null,
          drawerExpanded: false,
        });
      },

      // 점검 클릭 핸들러
      handleInspectionClick: (site: SiteData, event?: React.MouseEvent) => {
        event?.stopPropagation();
        set({
          selectedSite: site,
          selectedSiteId: site.siteId,
          activeDrawer: 'inspection',
        });
      },

      // 점검 드로어 닫기
      closeInspectionDrawer: () => {
        set({
          selectedSite: null,
          selectedSiteId: null,
          activeDrawer: null,
        });
      },

      // 상세 클릭 핸들러
      handleDetailClick: (site: SiteData, event?: React.MouseEvent) => {
        event?.stopPropagation();
        set({
          selectedSite: site,
          selectedSiteId: site.siteId,
          activeDrawer: 'detail',
        });
      },

      // 상세 드로어 닫기
      closeDetailDrawer: () => {
        set({
          selectedSite: null,
          selectedSiteId: null,
          activeDrawer: null,
        });
      },

      // 드로어 확장 상태 설정
      setDrawerExpanded: (expanded: boolean) => {
        set({ drawerExpanded: expanded });
      },

      // 선택된 시간 기간 설정
      setSelectedTimePeriod: (period: string) => {
        set({ selectedTimePeriod: period });
      },

      // 사이트 데이터 복원 (새로고침 후)
      restoreSiteData: (site: SiteData) => {
        const { selectedSiteId } = get();
        if (selectedSiteId === site.siteId) {
          set({ selectedSite: site });
        }
      },

      // URL에서 사이트 ID 추출 (필요시 사용)
      getSiteIdFromUrl: () => {
        const params = new URLSearchParams(window.location.search);
        return params.get('siteId');
      },
    }),
    {
      name: 'drawer-storage', // localStorage key name
      storage: createJSONStorage(() => localStorage),
      // persist할 상태만 선별 (selectedSite는 제외)
      partialize: (state) => ({
        selectedSiteId: state.selectedSiteId,
        activeDrawer: state.activeDrawer,
        drawerExpanded: state.drawerExpanded,
        selectedTimePeriod: state.selectedTimePeriod,
      }),
    }
  )
);

// 컴포넌트에서 사용하기 쉽게 selector 제공
export const useDrawerSelectors = () => {
  const store = useDrawerStore();

  return {
    ...store,
    // 호환성을 위한 계산된 값들
    inspectionDrawerOpen: store.activeDrawer === 'inspection',
    selectedSiteForInspection:
      store.activeDrawer === 'inspection' ? store.selectedSite : null,
    detailDrawerOpen: store.activeDrawer === 'detail',
    selectedSiteForDetail:
      store.activeDrawer === 'detail' ? store.selectedSite : null,
  };
};
